import type { RefundProtectQuoteRequest } from 'api/insurance'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import { getFlexibleCancellationDays, isFlexibleCancellationPolicy, isWithinChangeOfMindPolicy } from 'lib/offer/cancellationPolicyUtils'
import moment from 'moment'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import getObjectKey from 'lib/object/getObjectKey'
import { getCurrentIntl } from 'i18n/util/currentIntl'

interface CancellationPolicyInfo {
  cancellationType?: 'free' | 'flexible'
  cancelUntilFullRefundDate: moment.Moment
  cancelAfterNonRefundableDate?: moment.Moment
  cancellationAfterChangeOfMindPeriod: boolean
}

export function getBookingProtectionQuoteKey(quotePayload: RefundProtectQuoteRequest | null): string {
  if (!quotePayload) {
    return 'default'
  }

  return getObjectKey(quotePayload)
}

function showFreeCancellation(
  checkInDate?: string | moment.Moment,
  cancellationPolicy?: App.OfferCancellationPolicy,
) {
  if (!cancellationPolicy) {
    return false
  }
  if (cancellationPolicy.type !== 'post-purchase-seven-days') {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    return moment(checkInDate).subtract(21, 'days').isAfter(moment().startOf('day'))
  }
}

function showFlexibleCancellation(
  checkInDate?: string,
  cancellationPolicy?: App.OfferCancellationPolicy,
) {
  if (!cancellationPolicy) {
    return false
  }
  if (!isFlexibleCancellationPolicy(cancellationPolicy)) {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    const days = getFlexibleCancellationDays(cancellationPolicy.type)
    const withinChangeOfMind = isWithinChangeOfMindPolicy(new Date(), checkInDate)
    return withinChangeOfMind || moment(checkInDate).subtract(days, 'days').isAfter(moment().startOf('day'))
  }
}

export function isBookingProtectionAvailableForAccommodationOffer(offer: App.AnyOffer): boolean {
  if (offer.parentType === OFFER_TYPE_HOTEL) {
    if (offer.type === OFFER_TYPE_ALWAYS_ON) return true
    if (offer.type === OFFER_TYPE_HOTEL) return true
    if (offer.type === OFFER_TYPE_BED_BANK) return true
  }

  return false
}

export function getCancellationPolicyInfo(
  checkInDate?: string,
  cancellationPolicy?: App.OfferCancellationPolicy,
): CancellationPolicyInfo {
  const freeCancellation = showFreeCancellation(checkInDate, cancellationPolicy)
  const flexibleCancellation = showFlexibleCancellation(checkInDate, cancellationPolicy)
  const cancellationType = freeCancellation ? 'free' : flexibleCancellation ? 'flexible' : undefined
  let startBasedOnToday: moment.Moment = moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let cancelUntilFullRefundDate: moment.Moment = moment()
  let cancelAfterNonRefundableDate: moment.Moment | undefined
  let cancellationAfterChangeOfMindPeriod = false

  if (freeCancellation) {
    const startBasedOnCheckIn = checkInDate && moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days')
    if (startBasedOnCheckIn) {
      cancelUntilFullRefundDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
    }
  } else if (flexibleCancellation && cancellationPolicy && checkInDate) {
    const days = getFlexibleCancellationDays(cancellationPolicy.type)
    let startBasedOnCheckIn = moment(checkInDate).subtract(days, 'days')

    const changeOfMindDeadline = moment(checkInDate).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
    startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

    cancellationAfterChangeOfMindPeriod = startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days'))
    startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

    cancelUntilFullRefundDate = startBasedOnToday
    cancelAfterNonRefundableDate = startBasedOnCheckIn
  }

  return {
    cancellationType,
    cancelUntilFullRefundDate,
    cancelAfterNonRefundableDate,
    cancellationAfterChangeOfMindPeriod,
  }
}

export function getBookingProtectionCancellationReasons() {
  const intl = getCurrentIntl()
  return [
    intl.formatMessage({ id: 'oi2Ctvn2yMk3bg5eRG7P', defaultMessage: 'Illness/injury (including Covid)', description: 'Booking protection cancellation reason for illness or injury' }),
    intl.formatMessage({ id: 'SXLINs-TUdldONIucUlU', defaultMessage: 'Home emergency', description: 'Booking protection cancellation reason for home emergency' }),
    intl.formatMessage({ id: 'pLPIb8j6BIpRrUV7V5_E', defaultMessage: 'Pre-existing medical condition', description: 'Booking protection cancellation reason for pre-existing medical condition' }),
    intl.formatMessage({ id: 'lEH_tioEKQjC4oKBconL', defaultMessage: 'Flight disruption', description: 'Booking protection cancellation reason for flight disruption' }),
    intl.formatMessage({ id: '0vhJjGwcSMS3_ICsTyJr', defaultMessage: 'Adverse weather', description: 'Booking protection cancellation reason for adverse weather' }),
    intl.formatMessage({ id: 'AypokdfXNrkaLWv4YLWI', defaultMessage: 'Theft of documents', description: 'Booking protection cancellation reason for theft of documents' }),
    intl.formatMessage({ id: 'UeClXNpPuCbBU5SQ-Vi0', defaultMessage: 'Workplace redundancy', description: 'Booking protection cancellation reason for workplace redundancy' }),
    intl.formatMessage({ id: 'FQZw9eb4Iinjy4Yv3XVB', defaultMessage: 'Jury service', description: 'Booking protection cancellation reason for jury service' }),
    intl.formatMessage({ id: '7FCqIOtRBcEQfqaP5HfI', defaultMessage: 'Court summons', description: 'Booking protection cancellation reason for court summons' }),
    intl.formatMessage({ id: 'R9ENiPb2yI1eO6f0_cXG', defaultMessage: 'Pregnancy complications', description: 'Booking protection cancellation reason for pregnancy complications' }),
    intl.formatMessage({ id: 'tVNPCJ9rUdkZVRBUE5lI', defaultMessage: 'Death of immediate family member', description: 'Booking protection cancellation reason for death of family member' }),
    intl.formatMessage({ id: 'pXovonDuSdA385QMkO0y', defaultMessage: 'Mechanical breakdown', description: 'Booking protection cancellation reason for mechanical breakdown' }),
    intl.formatMessage({ id: 'p2VVNUhnM3mpXby4zQV0', defaultMessage: 'Relocated for work', description: 'Booking protection cancellation reason for work relocation' }),
    intl.formatMessage({ id: 'lHKI-33arxV1q_i5a_g2', defaultMessage: 'Armed forces & emergency services recall', description: 'Booking protection cancellation reason for armed forces recall' }),
    intl.formatMessage({ id: 'FOEajdG9K97ZeuDUFizY', defaultMessage: 'Changes to examination dates', description: 'Booking protection cancellation reason for exam date changes' }),
  ]
}

export function getFlightsBookingProtectionCancellationReasons() {
  const intl = getCurrentIntl()
  return [
    intl.formatMessage({ id: 'HcXEPXhqL92ipN6-Kely', defaultMessage: 'Illness/injury (including Covid)', description: 'Flight booking protection cancellation reason for illness or injury' }),
    intl.formatMessage({ id: 'j1X4L5nQzOugvEXbJ7_S', defaultMessage: 'Pre-existing medical condition', description: 'Flight booking protection cancellation reason for pre-existing medical condition' }),
    intl.formatMessage({ id: '7bpc8Ul156N68bvsJnmi', defaultMessage: 'Court summons', description: 'Flight booking protection cancellation reason for court summons' }),
    intl.formatMessage({ id: '29Rg9N5n0bqf76VZe_UJ', defaultMessage: 'Workplace redundancy', description: 'Flight booking protection cancellation reason for workplace redundancy' }),
    intl.formatMessage({ id: 'MgY4k4so6UiDPajiI9qh', defaultMessage: 'Death of immediate family', description: 'Flight booking protection cancellation reason for death of family member' }),
    intl.formatMessage({ id: 'QeDQCpWpy7ljVclnX_B4', defaultMessage: 'Public transport failure', description: 'Flight booking protection cancellation reason for public transport failure' }),
    intl.formatMessage({ id: 'Dc8lFJtQUZdC6PgEnsrL', defaultMessage: 'Relocated for work', description: 'Flight booking protection cancellation reason for work relocation' }),
    intl.formatMessage({ id: 'WpPUTx0Q9G_Xb1J3-pZj', defaultMessage: 'Armed forces & emergency services recall', description: 'Flight booking protection cancellation reason for armed forces recall' }),
    intl.formatMessage({ id: 'ZxtPa64DcJnPSXZQE1Lc', defaultMessage: 'Home emergency', description: 'Flight booking protection cancellation reason for home emergency' }),
    intl.formatMessage({ id: 'nArekNfUQrddBMbtjRYM', defaultMessage: 'Theft of documents', description: 'Flight booking protection cancellation reason for theft of documents' }),
    intl.formatMessage({ id: 'hhH-99LIS2iJ-79dqb7K', defaultMessage: 'Pregnancy complication', description: 'Flight booking protection cancellation reason for pregnancy complication' }),
    intl.formatMessage({ id: '_IfxfIrufKH49GZb7u2m', defaultMessage: 'Jury service', description: 'Flight booking protection cancellation reason for jury service' }),
    intl.formatMessage({ id: '263IK76dNv3fkaXFC7YU', defaultMessage: 'Flight disruption', description: 'Flight booking protection cancellation reason for flight disruption' }),
    intl.formatMessage({ id: 'QMh_pt4zUizw-AnF-ucR', defaultMessage: 'Mechanical breakdown', description: 'Flight booking protection cancellation reason for mechanical breakdown' }),
    intl.formatMessage({ id: 'R60Rlc4_QfmAYjaJjslD', defaultMessage: 'Adverse weather', description: 'Flight booking protection cancellation reason for adverse weather' }),
    intl.formatMessage({ id: 'ajhjfvDwQlTgcfJDEEBp', defaultMessage: 'Changes to examination dates', description: 'Flight booking protection cancellation reason for exam date changes' }),
  ]
}
